import { ClientFetch } from '../../fetch/ClientFetch';
import type { CustomerReviewsResponse } from '../../../types/customerReviews';
import {
  type LocaleIso,
  buildBrandLanguageCountryParam,
  getCountryByLocale,
  HTTP_METHODS,
  REVIEWS_LOCALE_FALLBACK_MAP,
} from 'utils';

interface FetchReviewsDataType {
  start?: number;
  limit?: number;
  onlyComments?: boolean;
  rating?: number;
  setSpecification?: string;
  productId?: number;
  locale?: LocaleIso;
}

/**
 * Fetches customer reviews by product specification. This function is intended for client-side usage and utilizes `ClientFetch.makePsiRequest` to
 * communicate with the PSI service endpoint `product.customerFeedback.getOverviewByProductAlternatives`.
 * For new locales (PL, HU, BG, CS) that may not have reviews, falls back to en-GB reviews.
 * @param data - The data for fetching reviews. It should include the following fields:
 *   - start?: Zero-based index to start pagination (default: 0).
 *   - limit?: Maximum number of reviews to return.
 *   - onlyComments?: If true, fetch only reviews that include comments.
 *   - rating?: Filter reviews by rating (1–5).
 *   - setSpecification?: Product specification string.
 *   - productId?: Identifier of the product to retrieve reviews for.
 *   - locale?: The locale to use for fetching reviews (will be mapped via REVIEWS_LOCALE_FALLBACK_MAP if needed).
 * @returns A promise that resolves to the customer reviews response.
 */
export async function fetchReviews(data: FetchReviewsDataType): Promise<CustomerReviewsResponse> {
  // Apply locale fallback mapping for new locales that don't have reviews yet
  const requestLocale = data.locale ? (REVIEWS_LOCALE_FALLBACK_MAP[data.locale] ?? data.locale) : undefined;

  // Create locale-based parameters (cl, languageId, countryId, brandId) from the mapped locale
  let localeParams = {};

  if (requestLocale) {
    const country = getCountryByLocale(requestLocale);

    localeParams = {
      languageId: country.languageId,
      countryId: country.countryId,
      brandId: country.brandId,
      cl: buildBrandLanguageCountryParam(requestLocale),
    };
  }

  // ClientFetch stringifies undefined to "undefined"; std-lib sent an empty value, so drop optional fields that are not set
  const payload = Object.fromEntries(
    Object.entries({ ...data, ...localeParams }).filter(([, value]) => value !== undefined),
  );

  return new ClientFetch().makePsiRequest<CustomerReviewsResponse>(
    'product.customerFeedback.getOverviewByProductAlternatives',
    payload,
    { method: HTTP_METHODS.POST },
  );
}
